import { useCallback } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFreezeCard } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Types & Schemas */
export const FreezeCardFormSchema = z.object({
  cardId: z
    .string({
      message: 'Card ID is required',
    })
    .refine(
      (cardId) => {
        return !isNaN(Number(cardId));
      },
      { message: 'Card ID must be a number' },
    ),
});

export type FreezeCardFormType = z.infer<typeof FreezeCardFormSchema>;

/* Internal components */
type FreezeCardFormProps = {
  handleFreezeCard: (cardId: number) => Promise<void>;
};

const FreezeCardForm = ({ handleFreezeCard }: FreezeCardFormProps) => {
  const form = useForm<FreezeCardFormType>({
    resolver: zodResolver(FreezeCardFormSchema),
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: FreezeCardFormType) => {
      const { cardId } = data;

      try {
        await handleFreezeCard(Number(cardId));
        form.reset();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [form, handleFreezeCard],
  );

  /* Render */
  return (
    <Form {...form}>
      <form
        className='w-full flex flex-col gap-2'
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name='cardId'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{'Card ID'}</FormLabel>
              <FormControl>
                <Input {...field} placeholder='1234567890' type='text' />
              </FormControl>
              <FormMessage className='text-start' />
            </FormItem>
          )}
        />
        <Button
          className='w-full cursor-pointer uppercase'
          type='submit'
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'freezing...' : 'freeze'}
        </Button>
      </form>
    </Form>
  );
};

/* Main component */
export const FreezeCard = () => {
  const { handleFreezeCard } = useFreezeCard();

  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <FreezeCardForm handleFreezeCard={handleFreezeCard} />
    </section>
  );
};
